import { FormControlLabel, Typography } from '../../UIKit';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useLanguage, useHasAccess } from '~/Hooks';
import { List, Radio, RadioGroup, useTheme, useMediaQuery, Link } from '@mui/material';
import ListItemSelect from '../../Screens/Events/EventPage/Components/ListItemSelect';

import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { requestSportCategories } from '~/store/events';
import { CategoryList } from '~/Components/Icons/CategoryList';
import { SportCategoryListStyles } from './SportCategoryList.styles';
import { SportCategory } from '~/Models/DTO/SportCatogories.model';
import { PopoverToModal, SearchTextField } from '~/Components';
import { requestDismissWarning, selectIsWarningDismissed } from '../../store/user';
import { AccessList } from '~/Hooks/useHasAccess/accessList.types';
import { useSportCatgeroy } from '~/Helpers/SportCategory';

interface SportCategoryListProps {
  open: boolean;
  onClose: () => void;
  updateSportCategoryType: (type: number) => void;
  selectedSportCategoryType: number;
  anchorEl?: null | Element;
  sportCategories: SportCategory[];
}

export const useSportCategoryList = ({
  updateSportCategoryTypeProp,
  selectedSportCategoryType,
  onClose,
  sportCategories,
  open,
}: {
  updateSportCategoryTypeProp: (type: number) => void;
  selectedSportCategoryType: number;
  onClose: () => void;
  sportCategories: SportCategory[];
  open: boolean;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const dispatch = useDispatch();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hasAccess } = useHasAccess();
  const { getCategory } = useSportCatgeroy();
  const dismissWarning = () =>
    bindActionCreators(requestDismissWarning, dispatch)('cooperationCategoryChangeWarning');

  const showWarning =
    !useSelector(selectIsWarningDismissed('cooperationCategoryChangeWarning')) &&
    hasAccess(AccessList.HIDE_SPORTS_CATEGORY_WARNING_PARTNER_TOOL);

  const getSportCategories = bindActionCreators(requestSportCategories, dispatch);
  const [sportCategoryType, setSportCategoryType] = useState(selectedSportCategoryType);

  const filteredSportCategories = sportCategories.filter((sportCategoryType) =>
    getCategory(sportCategoryType.id).toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const updateSportCategoryType = async () => {
    updateSportCategoryTypeProp(sportCategoryType);
    onClose();
  };

  useEffect(() => {
    if (open) {
      setSearchQuery('');
    }
  }, [open]);

  useEffect(() => {
    if (!mobile) {
      updateSportCategoryType();
    }
  }, [sportCategoryType]);

  useEffect(() => {
    if (!sportCategories.length) {
      getSportCategories();
    }
  }, []);

  return {
    filteredSportCategories,
    doSearch: setSearchQuery,
    updateSportCategoryType,
    setSportCategoryType,
    sportCategoryType,
    dismissWarning,
    showWarning,
  };
};

export const SportCategoryList = ({
  open,
  onClose,
  selectedSportCategoryType,
  updateSportCategoryType: updateSportCategoryTypeProp,
  anchorEl,
  sportCategories,
}: SportCategoryListProps) => {
  const {
    doSearch,
    updateSportCategoryType,
    setSportCategoryType,
    sportCategoryType,
    filteredSportCategories,
    dismissWarning,
    showWarning,
  } = useSportCategoryList({
    updateSportCategoryTypeProp,
    selectedSportCategoryType,
    onClose,
    sportCategories,
    open,
  });

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    doSearch(event.currentTarget.value);
  };

  const { classes } = SportCategoryListStyles()();
  const { t } = useLanguage();
  const { getCategory } = useSportCatgeroy();
  const getName = (type: any) => getCategory(type);

  if (!anchorEl) {
    return null;
  }

  return (
    <PopoverToModal
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      title={t('app.event.categories')}
      actionName={t('app.checkin.select.done')}
      onActionTaken={updateSportCategoryType}
    >
      <div className={classes.content}>
        {showWarning && (
          <div className={classes.note}>
            <Typography className={classes.infoText}>
              <strong className={classes.noteHighlighted}>{t('app.note')}&nbsp;</strong>
              {t('app.event.changeCategory.cooperationWarning')}
            </Typography>
            <Typography className={classes.infoText}>
              <Link onClick={dismissWarning} underline="hover">
                {t('app.doNotShowThisAgain')}
              </Link>
            </Typography>
          </div>
        )}

        <div className={classes.searchContainer}>
          <SearchTextField
            id="searchSportCategoryTypes"
            onChange={handleSearch}
            placeholder={t('app.event.changeEventCategoryType.searchForNewEventCategoryType')}
            withBackground
          />
        </div>
        <List className={classes.sportCategoryTypeList} data-testid="ChangeSportCategoryTypeList">
          {filteredSportCategories.map((type: SportCategory) => (
            <ListItemSelect
              key={type.id}
              onClick={() => setSportCategoryType(type.id)}
              icon={<CategoryList />}
              name={getName(type.id)}
              testId={'ChangeSportCategoryTypeListItem'}
            >
              <RadioGroup
                aria-label="sportCategoryType"
                name="sportCategoryType"
                value={selectedSportCategoryType}
                onChange={() => setSportCategoryType(type.id)}
                className={classes.radioGroup}
              >
                <FormControlLabel
                  value={type}
                  control={<Radio />}
                  label=""
                  className={classes.radio}
                  checked={sportCategoryType === type.id}
                />
              </RadioGroup>
            </ListItemSelect>
          ))}
        </List>
      </div>
    </PopoverToModal>
  );
};
